// The original SceneDesigner layout — top bar across the top, catalog drawer on
// the left, side menu floating over the viewport, summary bottom-right, cart
// modal opened from the summary's "View list" button. Generic enough to be the
// default for new Designers.
//
// Pure renderer: takes LayoutProps from SceneDesignerTool, never fetches, never
// owns scene state. The cart is rendered at the host root via the modal stack;
// this layout just triggers `openModal("cart")` from the summary bar.

import { useMemo } from "react";
import { ConfiguratorViewer } from "../../../components/ConfiguratorViewer";
import { Slot } from "../blocks/Slot";
import { mergeSlotMappings } from "../blocks/slotMapping";
import type { SlotMapping } from "../blocks/types";
import { CatalogPanel } from "../components/CatalogPanel";
import { SummaryBar } from "../components/SummaryBar";
import { TopBar } from "../components/TopBar";
import { ViewportSideMenu } from "../components/ViewportSideMenu";
import type { LayoutProps } from "./types";

/**
 * Default slot mapping for the standard layout. Only the on-selection popup
 * is slot-driven today; every other component is hardcoded. Lives inline
 * because it is one entry — a sibling defaults.ts file would be overkill.
 * Mirrors the single-menu default: live name + line + per-surface picker.
 */
const STANDARD_DEFAULT_MAPPING: SlotMapping = {
  "selection-popup": [
    {
      block: "text", key: "selected-name",
      props: { variant: "heading", source: "from-selected-product-name" },
    },
    { block: "line", key: "name-line" },
    { block: "selection-surfaces", key: "surfaces" },
  ],
};

export function StandardLayout(props: LayoutProps) {
  const {
    visibleCategories,
    products,
    viewer,
    layoutFlags: layout,
    instances,
    selectedInstance,
    selectedInstanceId,
    selectedProductName,
    viewerRef,
    viewerReady,
    setViewerReady,
    onUnityEvent,
    baseUrl,
    apiBaseUrl,
    isFpsCamera,
    catalogOpen,
    setCatalogOpen,
    openModal,
    blockHost,
    slotMappingOverride,
    sendCommand,
    onResetScene,
    onDeleteSelected,
  } = props;

  const selectionVisible = layout.showSelection && selectedInstance != null;

  // Per-slot override-wins merge against the layout default. Only used for
  // selection-popup today; other components stay hardcoded.
  const mapping = useMemo(
    () => mergeSlotMappings(STANDARD_DEFAULT_MAPPING, slotMappingOverride),
    [slotMappingOverride]
  );

  return (
    <>
      {layout.showTopBar && (
        <TopBar
          onToggleCatalog={layout.showCatalog ? () => setCatalogOpen(!catalogOpen) : undefined}
          onResetScene={onResetScene}
        />
      )}

      <div className="sd__body">
        {layout.showCatalog && (
          <CatalogPanel
            categories={visibleCategories}
            products={products}
            isOpen={catalogOpen}
            onRequestClose={() => setCatalogOpen(false)}
          />
        )}

        <div className="sd__viewport">
          {viewer ? (
            <ConfiguratorViewer
              ref={viewerRef}
              viewer={viewer}
              baseUrl={baseUrl}
              apiBaseUrl={apiBaseUrl}
              enableDragAndHotkeys
              onReady={() => setViewerReady(true)}
              onUnityEvent={onUnityEvent}
            />
          ) : (
            <div className="sd__viewport-placeholder">
              <div className="sd__viewport-icon">🛠</div>
              <p>No designer target configured.</p>
              <p className="sd__viewport-hint">
                Add <code>?target=&lt;designer-target&gt;</code> to the URL or pin one on the Designer.
              </p>
            </div>
          )}

          {layout.showSideMenu && viewer && viewerReady && (
            <ViewportSideMenu
              hasSelection={selectedInstanceId != null}
              isFpsCamera={isFpsCamera}
              onCommand={sendCommand}
              onDeleteSelected={onDeleteSelected}
            />
          )}

          {layout.showSummary && (
            <SummaryBar
              itemCount={instances.length}
              selectedProductName={selectedProductName}
              onOpenCart={layout.showCart ? () => openModal("cart") : undefined}
            />
          )}

          {/* On-selection popup — same slot id as single-menu so a
              Designer's override applies regardless of which layout is
              chosen. Standard's card chrome is plainer; the block-level
              styles (sd-block-selection-surfaces) are shared. */}
          {selectionVisible && (
            <Slot
              slotId="selection-popup"
              mapping={mapping["selection-popup"]}
              host={blockHost}
              className="sd-selection-card sd-selection-card--open"
            />
          )}
        </div>
      </div>
    </>
  );
}
